/**
 * InstantProofPanel Component
 * 
 * Post-submission feedback panel showing:
 * - Confirmation message
 * - Metrics delta (baseline → replica)
 * - Savings summary
 * - Explainability block
 * - "Add to Exam Set" button
 * - Lock message for daily completion
 */

import React from 'react';
import { Check, TrendingUp, Clock, Shield, BookOpen, Lock, ArrowRight } from 'lucide-react';
import type { CaseEvaluation, TrainingAction, DraftResult } from '../../types/trainingTypes';
import { formatTime, formatPercentage, getScoreColorClass, getActionLabel } from '../../lib/scoringUtils';

interface InstantProofPanelProps {
    action: TrainingAction;
    evaluation: CaseEvaluation;
    baseline: DraftResult;
    replica: DraftResult;
    onAddToExamSet: () => void;
    isAddedToExamSet: boolean;
}

export const InstantProofPanel: React.FC<InstantProofPanelProps> = ({
    action,
    evaluation,
    baseline,
    replica,
    onAddToExamSet,
    isAddedToExamSet
}) => {
    const replicaWon = evaluation.representationQuality > 60;

    return (
        <div className="space-y-6">
            {/* Confirmation Banner */}
            <div className="card-elevated p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-4 flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-semibold text-hig-text-primary mb-2">
                    Replica Trained Today
                </h2>
                <p className="text-hig-text-secondary">
                    Training action: <span className="font-medium text-hig-text-primary">{getActionLabel(action)}</span>
                </p>
            </div>

            {/* Metrics Delta */}
            <div className="card-elevated p-6 space-y-4">
                <h3 className="text-sm font-semibold text-hig-text-tertiary uppercase tracking-wide">
                    Performance Comparison
                </h3>

                <div className="grid grid-cols-2 gap-4">
                    {/* Baseline Metrics */}
                    <div className="p-4 bg-hig-bg-secondary rounded-lg border border-hig-border">
                        <div className="text-xs text-hig-text-tertiary uppercase tracking-wide mb-3">
                            Generic AI (Before)
                        </div>
                        <div className="space-y-2">
                            <MetricRow label="Edits Required" value={baseline.estimatedEdits.toString()} />
                            <MetricRow label="Time to Fix" value={formatTime(baseline.estimatedTimeSeconds)} />
                            <MetricRow label="Issues" value={baseline.issuesDetected.length.toString()} />
                        </div>
                    </div>

                    {/* Replica Metrics */}
                    <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/30">
                        <div className="text-xs text-green-400 uppercase tracking-wide mb-3">
                            Your Replica (After)
                        </div>
                        <div className="space-y-2">
                            <MetricRow
                                label="Edits Required"
                                value={replica.estimatedEdits.toString()}
                                improved={replica.estimatedEdits < baseline.estimatedEdits}
                            />
                            <MetricRow
                                label="Time to Fix"
                                value={formatTime(replica.estimatedTimeSeconds)}
                                improved={replica.estimatedTimeSeconds < baseline.estimatedTimeSeconds}
                            />
                            <MetricRow
                                label="Issues"
                                value={replica.issuesDetected.length.toString()}
                                improved={replica.issuesDetected.length < baseline.issuesDetected.length}
                            />
                        </div>
                    </div>
                </div>

                {/* Delta Arrow */}
                <div className="flex items-center justify-center gap-4 py-4">
                    <ArrowRight className="w-5 h-5 text-hig-text-tertiary" />
                </div>

                {/* Representation Quality Score */}
                <div className="text-center p-6 bg-hig-bg-secondary rounded-xl border border-hig-border">
                    <div className="text-xs text-hig-text-tertiary uppercase tracking-wide mb-2">
                        Representation Quality
                    </div>
                    <div className={`text-5xl font-bold ${getScoreColorClass(evaluation.representationQuality)}`}>
                        {evaluation.representationQuality}
                    </div>
                    <div className="text-sm text-hig-text-tertiary mt-1">out of 100</div>

                    {/* Component Scores */}
                    <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-hig-divider">
                        <ScoreComponent label="Style" value={evaluation.styleMatch} />
                        <ScoreComponent label="Correct" value={evaluation.correctness} />
                        <ScoreComponent label="Safety" value={evaluation.safety} />
                        <ScoreComponent label="Escalation" value={evaluation.escalationAccuracy} />
                    </div>
                </div>
            </div>

            {/* Savings Summary */}
            <div className="card-elevated p-6">
                <h3 className="text-sm font-semibold text-hig-text-tertiary uppercase tracking-wide mb-4">
                    Savings Summary
                </h3>

                <div className="grid grid-cols-2 gap-4">
                    <SavingsCard
                        icon={<Clock className="w-5 h-5" />}
                        label="Time Saved"
                        value={formatTime(evaluation.savingsSeconds)}
                        subtext="per task"
                    />
                    <SavingsCard
                        icon={<TrendingUp className="w-5 h-5" />}
                        label="Correction Reduction"
                        value={formatPercentage(evaluation.correctionReductionPct)}
                        subtext="fewer edits"
                    />
                </div>
            </div>

            {/* Explainability Block */}
            <div className="card-elevated p-6">
                <h3 className="text-sm font-semibold text-hig-text-tertiary uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {replicaWon ? 'Why Replica Performed Well' : 'Areas for Improvement'}
                </h3>

                <ul className="space-y-2">
                    {evaluation.whyReplicaWon.map((reason, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-hig-text-secondary">
                            <span className={replicaWon ? 'text-green-400' : 'text-yellow-400'}>•</span>
                            {reason}
                        </li>
                    ))}
                </ul>

                {evaluation.riskFlagsAvoided.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-hig-divider">
                        <div className="text-xs text-hig-text-tertiary uppercase tracking-wide mb-2">
                            Risk Flags Avoided
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {evaluation.riskFlagsAvoided.map((flag, idx) => (
                                <span
                                    key={idx}
                                    className="px-2 py-1 text-xs bg-green-500/10 text-green-400 rounded border border-green-500/30"
                                >
                                    {flag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Add to Exam Set */}
            <div className="card-elevated p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 text-hig-text-tertiary" />
                        <div>
                            <div className="font-medium text-hig-text-primary">Add to Exam Set</div>
                            <div className="text-xs text-hig-text-tertiary">
                                Use this case for future Replica validation
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onAddToExamSet}
                        disabled={isAddedToExamSet}
                        className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${isAddedToExamSet
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
                                : 'bg-hig-bg-tertiary text-hig-text-primary border border-hig-border hover:bg-hig-bg-elevated'
                            }
            `}
                    >
                        {isAddedToExamSet ? 'Added' : 'Add'}
                    </button>
                </div>
            </div>

            {/* Lock Message */}
            <div className="p-6 bg-hig-bg-tertiary rounded-xl border border-hig-border text-center">
                <Lock className="w-6 h-6 text-hig-text-tertiary mx-auto mb-3" />
                <p className="text-lg font-medium text-hig-text-primary">
                    Done for today
                </p>
                <p className="text-sm text-hig-text-tertiary mt-1">
                    Come back tomorrow for your next training session
                </p>
            </div>
        </div>
    );
};

// ============================================
// METRIC ROW SUB-COMPONENT
// ============================================

interface MetricRowProps {
    label: string;
    value: string;
    improved?: boolean;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, value, improved }) => {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm text-hig-text-tertiary">{label}</span>
            <span className={`text-sm font-semibold ${improved ? 'text-green-400' : 'text-hig-text-primary'}`}>
                {value}
                {improved && <span className="ml-1">↓</span>}
            </span>
        </div>
    );
};

// ============================================
// SCORE COMPONENT SUB-COMPONENT
// ============================================

interface ScoreComponentProps {
    label: string;
    value: number;
}

const ScoreComponent: React.FC<ScoreComponentProps> = ({ label, value }) => {
    return (
        <div className="text-center">
            <div className={`text-lg font-semibold ${getScoreColorClass(value)}`}>
                {value}
            </div>
            <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wide">
                {label}
            </div>
        </div>
    );
};

// ============================================
// SAVINGS CARD SUB-COMPONENT
// ============================================

interface SavingsCardProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    subtext: string;
}

const SavingsCard: React.FC<SavingsCardProps> = ({ icon, label, value, subtext }) => {
    return (
        <div className="p-4 bg-hig-bg-secondary rounded-lg border border-hig-border text-center">
            <div className="text-hig-accent mb-2">{icon}</div>
            <div className="text-xs text-hig-text-tertiary uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-bold text-hig-text-primary mt-1">{value}</div>
            <div className="text-xs text-hig-text-tertiary">{subtext}</div>
        </div>
    );
};

export default InstantProofPanel;
